import * as React from 'react';

import { observer } from 'mobx-react';
import moment from 'moment';

import { getErrorMessage } from 'common-utils';
import { VersionUtils as vc } from 'common-utils/version-utils';
import {
    useForm,
    IconTextButton,
    BasicForm,
    Modal,
    showConfirmDialogWithoutCancel,
    FormItem,
    SelectFormItem,
    DateTimePicker,
} from 'ui-lib';
import { ILogOptions, DOWNLOAD_LOG_TYPE, DEVICE_LOG_TYPE } from 'dto/device-dto';
import { hasContainsIgnoreCase } from 'common-utils/string-utils';
import { getDayJsInstance } from 'common-utils/date-utils';

import { DeviceLogsTable, exportDeviceLogs, getDeviceLogsColumn, IDeviceLogsProps, useDeviceLogsStore } from './device-log-table';

const logTypeDataSource = Object.values(DOWNLOAD_LOG_TYPE).map((item) => ({ value: item, label: item }));

export const AssureDeviceLogsComponent = observer((props: IDeviceLogsProps) => {
    const store = useDeviceLogsStore(props);

    const [isProcessing, setIsProcessing] = React.useState(false);
    const [modalVisible, setModalVisible] = React.useState<boolean>(false);
    const [form] = useForm();
    const handleCancel = () => {
        setModalVisible(false);
    };

    const columns = [
        getDeviceLogsColumn({
            filters: [{ text: 'TRAMPOLINE', value: 'TRAMPOLINE' }],
            onFilter: (type: string, item: ILogOptions) => item.data.toLowerCase().includes(type.toLowerCase()),
        }),
    ];

    const downloadLogFile = async (values: any) => {
        try {
            setIsProcessing(true);
            const deviceLogs = [];
            const downloadLogName = values.logTypeField.toLowerCase();
            const logType =
                values.logTypeField.toLowerCase() === DEVICE_LOG_TYPE.TERMLOG
                    ? DEVICE_LOG_TYPE.TERMLOG
                    : DEVICE_LOG_TYPE.SYSLOGS;
            if (
                values.logTypeField.toLowerCase() === DEVICE_LOG_TYPE.TERMLOG &&
                vc.lt(props.trampolineVersion, '3.1.12')
            ) {
                showConfirmDialogWithoutCancel({
                    title: 'Could not download Term log',
                    content: 'Please install Trampoline client 3.1.12 or higher version to download Term logs.',
                    okText: 'OK',
                });
                return;
            } else {
                store.displayDeviceLogs(logType).map((item) => {
                    if (values.logTypeField.toLowerCase() === DEVICE_LOG_TYPE.TERMLOG) {
                        deviceLogs.push(item.data);
                    } else {
                        if (hasContainsIgnoreCase(item.data, values.logTypeField)) {
                            deviceLogs.push(item.data);
                        }
                    }
                });
            }
            exportDeviceLogs(store, downloadLogName, deviceLogs);
        } catch (err) {
            showConfirmDialogWithoutCancel({
                title: 'Error',
                content: getErrorMessage(err),
                okText: 'OK',
            });
        } finally {
            setIsProcessing(false);
            setModalVisible(false);
        }
    };

    const LogModal = () => {
        return (
            <Modal
                isVisible={modalVisible}
                isClosable={true}
                onCancel={handleCancel}
                width={500}
                extraProps={{ footer: null }}
                title='Provide the download log type'
            >
                <BasicForm
                    form={form}
                    key='addOrEdit'
                    cardPros={{ bordered: false }}
                    items={[
                        <SelectFormItem
                            key='logTypeField'
                            code='logTypeField'
                            isRequired={true}
                            label='Log Type'
                            labelAlign='right'
                            labelColSpan={5}
                            placeholder='Select Log Type'
                            dataSource={logTypeDataSource}
                        />,
                        <FormItem
                            key='download'
                            code='button'
                            label=' '
                            labelAlign='right'
                            labelCol={{ span: 5 }}
                            colon={false}
                            child={
                                <IconTextButton
                                    key='save'
                                    label='Download'
                                    type='primary'
                                    htmlType='submit'
                                    loading={isProcessing}
                                />
                            }
                        />,
                    ]}
                    onSubmit={downloadLogFile}
                />
            </Modal>
        );
    };

    return (
        <DeviceLogsTable
            store={store}
            columns={columns}
            isProcessing={isProcessing}
            onDownload={() => setModalVisible(true)}
            datePicker={
                <DateTimePicker
                    key='date-time-picker'
                    disabledDateType='DISABLE_FUTURE_DATES'
                    allowClear={false}
                    value={getDayJsInstance(store.dateFrom.toDate())}
                    onChange={(value): void => {
                        if (!value) return;
                        store.setDate(moment(value.toDate()));
                        store.initialize(true);
                    }}
                />
            }
        >
            <LogModal />
        </DeviceLogsTable>
    );
});
